import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  useMediaQuery,
} from "@mui/material";
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Group as GroupIcon,
  Storage as DataManagementIcon,
  People as AudienceIcon,
  Timeline as AttributionIcon,
  Notifications as NotificationsIcon,
  Settings as SettingsIcon,
} from "@mui/icons-material";

const SidebarLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const isMobile = useMediaQuery("(max-width:600px)");

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const isActive = (path) => location.pathname === path;

  const sidebarContent = (
    <List sx={{ width: "100%", padding: 0 }}>
      {/* Menu Items */}
      {[
        { label: "Dashboard", icon: <DashboardIcon />, path: "/dashboard" },
        { label: "Cohorts", icon: <GroupIcon />, path: "/cohort-charts" },
        { label: "Data Sources", icon: <DataManagementIcon />, path: "/integrations" },
        { label: "Audiences", icon: <AudienceIcon />, path: "/audiences" },
        { label: "Attribution", icon: <AttributionIcon />, path: "/attribution" },
        { label: "Notifications", icon: <NotificationsIcon />, path: "/notifications" },
        { label: "Settings", icon: <SettingsIcon />, path: "/settings" },
      ].map((item) => (
        <ListItem
          key={item.label}
          button
          onClick={() => navigate(item.path)}
          sx={{
            backgroundColor: isActive(item.path) ? "linear-gradient(90deg, #e0f7fa, #b2ebf2)" : "inherit",
            "&:hover": {
              backgroundColor: "rgba(224, 247, 250, 0.5)",
            },
            borderLeft: isActive(item.path) ? "4px solid #00acc1" : "4px solid transparent",
            transition: "background-color 0.3s, border-left 0.3s",
          }}
        >
          <ListItemIcon
            sx={{
              color: isActive(item.path) ? "#00acc1" : "inherit",
            }}
          >
            {item.icon}
          </ListItemIcon>
          <ListItemText
            primary={item.label}
            sx={{
              color: isActive(item.path) ? "#00acc1" : "inherit",
              fontWeight: isActive(item.path) ? "bold" : "normal",
            }}
          />
        </ListItem>
      ))}
    </List>
  );

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", backgroundColor: "#f9f9f9" }}>
      {/* Sidebar for Desktop */}
      {!isMobile && (
        <Box
          sx={{
            width: 250,
            background: "linear-gradient(180deg, #ffffff, #f1f1f1)",
            boxShadow: "0 2px 5px rgba(0, 0, 0, 0.1)",
            height: "100vh",
            position: "fixed",
          }}
        >
          {sidebarContent}
        </Box>
      )}

      {/* Drawer for Mobile */}
      {isMobile && (
        <>
          <IconButton
            sx={{
              position: "absolute",
              top: 10,
              left: 10,
              zIndex: 1200,
            }}
            onClick={handleDrawerToggle}
          >
            <MenuIcon />
          </IconButton>
          <Drawer
            anchor="left"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            ModalProps={{
              keepMounted: true,
            }}
          >
            <Box
              sx={{
                width: 250,
                background: "linear-gradient(180deg, #ffffff, #f1f1f1)",
                height: "100%",
              }}
            >
              {sidebarContent}
            </Box>
          </Drawer>
        </>
      )}

      {/* Main Content */}
      <Box
        sx={{
          flexGrow: 1,
          marginLeft: !isMobile ? "250px" : "0",
          padding: { xs: 2, md: 4 },
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export default SidebarLayout;
